import { Button, Input, Popconfirm, Space, Spin, Tag, Tooltip, Typography, Upload, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdAttachFile, MdDelete, MdEdit } from 'react-icons/md'

import { useNotification } from '@/Core/Hooks/useNotification'
import { SectionEmpty } from '@/Ship/Components/EntityCard'
import { formatDateTimeUk, UserChip } from '@/Ship/Helpers/fieldViews'

import { getErrorMessage } from '../../Helpers/getErrorMessage'
import { TaskCommentsTabStore } from '../../Stores/TaskCommentsTabStore'

import type { TaskComment, TaskFile, TaskUserRef } from '../../Contracts/Task.types'
import type { TaskFormContextLike } from '../../Contracts/Crud.types'
import type { UploadProps } from 'antd'

/** Тип опцій `customRequest` виводимо з `UploadProps` антда — без крихкого deep-import. */
type UploadRequestOption = Parameters<NonNullable<UploadProps['customRequest']>>[0]

const { Text, Paragraph } = Typography
const { TextArea } = Input

/** Клієнтський ліміт розміру файла коментаря (ФАКТ nginx = 10 MB, хвіст 08.2). */
const UPLOAD_MAX_SIZE_MB = 10

interface Props {
    formContext?: TaskFormContextLike
}

const userLabel = (u?: TaskUserRef | null): string =>
    u?.shortName || u?.fullName || [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.email || 'Користувач'

/**
 * Вкладка «Коментарі» картки задачі (SPEC-008 §4, наряд 08).
 *
 * Стрічка коментарів (автор, дата, текст, файли-лінки) + форма додавання
 * (textarea + файли через N13/`fileUuids`) + inline edit/delete своїх. Видимість
 * кнопок редагування/видалення — за `_permissions` кожного коментаря (бек рахує
 * автор∨admin — TaskCommentRule). Форма додавання показується лише коли задача
 * дозволяє коментувати (`task._permissions.comment` — false на видаленій).
 * Файли коментарів мають власну кнопку delete на наш DELETE-роут (хвіст 08.1).
 */
export const TaskCommentsTab = observer(({ formContext }: Props) => {
    const [store] = useState(() => new TaskCommentsTabStore())
    const { showSuccess, showError } = useNotification()
    const { token } = theme.useToken()

    const uuid: string | undefined = formContext?.values?.uuid
    const canComment = formContext?.values?._permissions?.comment === true

    useEffect(() => {
        void store.load(uuid)
    }, [store, uuid])

    const handleUpload = (options: UploadRequestOption): void => {
        const file = options.file as File
        if (file.size > UPLOAD_MAX_SIZE_MB * 1024 * 1024) {
            showError(`Файл завеликий. Максимальний розмір: ${UPLOAD_MAX_SIZE_MB} MB.`)
            options.onError?.(new Error('too large'))
            return
        }
        store
            .uploadDraftFile(file)
            .then(() => options.onSuccess?.({}))
            .catch((e: unknown) => {
                showError(getErrorMessage(e) || 'Не вдалося завантажити файл')
                options.onError?.(e as Error)
            })
    }

    const handleSubmit = (): void => {
        store
            .submit()
            .then((ok) => {
                if (ok) showSuccess('Коментар додано')
            })
            .catch((e: unknown) => showError(getErrorMessage(e) || 'Не вдалося додати коментар'))
    }

    const handleUploadEdit = (options: UploadRequestOption): void => {
        const file = options.file as File
        if (file.size > UPLOAD_MAX_SIZE_MB * 1024 * 1024) {
            showError(`Файл завеликий. Максимальний розмір: ${UPLOAD_MAX_SIZE_MB} MB.`)
            options.onError?.(new Error('too large'))
            return
        }
        store
            .uploadEditFile(file)
            .then(() => options.onSuccess?.({}))
            .catch((e: unknown) => {
                showError(getErrorMessage(e) || 'Не вдалося завантажити файл')
                options.onError?.(e as Error)
            })
    }

    const handleSaveEdit = (): void => {
        store
            .saveEdit()
            .then((ok) => {
                if (ok) showSuccess('Коментар змінено')
            })
            .catch((e: unknown) => showError(getErrorMessage(e) || 'Не вдалося змінити коментар'))
    }

    const handleRemove = (commentUuid: string): void => {
        store
            .remove(commentUuid)
            .then(() => showSuccess('Коментар видалено'))
            .catch((e: unknown) => showError(getErrorMessage(e) || 'Не вдалося видалити коментар'))
    }

    const handleDeleteFile = (commentUuid: string, fileUuid: string): void => {
        store
            .deleteCommentFile(commentUuid, fileUuid)
            .then(() => showSuccess('Файл видалено'))
            .catch((e: unknown) => showError(getErrorMessage(e) || 'Не вдалося видалити файл'))
    }

    const renderFiles = (comment: TaskComment): React.ReactNode => {
        const files = comment.files ?? []
        if (files.length === 0) return null
        const canManage = comment._permissions?.update === true || comment._permissions?.delete === true
        return (
            <Space direction="vertical" size={2} style={{ marginTop: 6 }}>
                {files.map((file: TaskFile) => (
                    <Space key={file.uuid} size={6}>
                        <a href={file.url} target="_blank" rel="noreferrer">
                            {file.originalName}
                        </a>
                        {canManage && (
                            <Popconfirm
                                title="Видалити файл?"
                                okText="Так"
                                cancelText="Ні"
                                onConfirm={() => handleDeleteFile(comment.uuid, file.uuid)}
                            >
                                <Button type="text" size="small" danger icon={<MdDelete />} />
                            </Popconfirm>
                        )}
                    </Space>
                ))}
            </Space>
        )
    }

    // Власна розмітка стрічки (БЕЗ antd `List`): List у antd v6 деприкейтнутий і
    // мемоїзує рядки, через що зміни MobX-стану (`editingUuid`), які читаються лише
    // всередині `renderItem`-колбека, не доходили до ре-рендера — кнопка «Редагувати»
    // виглядала мертвою. Прямий map у рендері observer-компонента реактивний.
    const renderComment = (comment: TaskComment): React.ReactNode => {
        const isEditing = store.editingUuid === comment.uuid
        const canUpdate = comment._permissions?.update === true
        const canDelete = comment._permissions?.delete === true

        return (
            <div key={comment.uuid} style={{ padding: '12px 0', borderBottom: `1px solid ${token.colorSplit}` }}>
                {/* Дії — іконками з підказкою: стрічка стоїть у вужчому стовпці
                    картки, і два підписані кнопки виштовхували дату на другий рядок */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <Space size={8} style={{ minWidth: 0 }}>
                        <Text strong>
                            <UserChip user={{ title: userLabel(comment.createdBy) }} />
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                            {formatDateTimeUk(comment.createdAt)}
                        </Text>
                    </Space>
                    {!isEditing && (
                        <Space size={0}>
                            {canUpdate && (
                                <Tooltip title="Редагувати">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<MdEdit />}
                                        aria-label="Редагувати коментар"
                                        onClick={() => store.startEdit(comment)}
                                    />
                                </Tooltip>
                            )}
                            {canDelete && (
                                <Popconfirm
                                    title="Видалити коментар?"
                                    okText="Так"
                                    cancelText="Ні"
                                    onConfirm={() => handleRemove(comment.uuid)}
                                >
                                    <Tooltip title="Видалити">
                                        <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<MdDelete />}
                                            aria-label="Видалити коментар"
                                        />
                                    </Tooltip>
                                </Popconfirm>
                            )}
                        </Space>
                    )}
                </div>
                <div style={{ marginTop: 6, paddingLeft: 32 }}>
                    {isEditing ? (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <TextArea
                                value={store.editingText}
                                onChange={(e) => store.setEditingText(e.target.value)}
                                rows={3}
                                maxLength={4096}
                            />
                            {/* Наявні файли коментаря: видалення діє одразу (N14) */}
                            {renderFiles(comment)}
                            {/* Нові файли цієї правки: прикріпляться на «Зберегти» */}
                            {store.editingFiles.length > 0 && (
                                <Space wrap>
                                    {store.editingFiles.map((file) => (
                                        <Tag key={file.uuid} closable onClose={() => void store.removeEditFile(file.uuid)}>
                                            {file.originalName}
                                        </Tag>
                                    ))}
                                </Space>
                            )}
                            <Space>
                                <Upload customRequest={handleUploadEdit} showUploadList={false} multiple>
                                    <Button size="small" icon={<MdAttachFile />} loading={store.uploadingEdit}>
                                        Файл
                                    </Button>
                                </Upload>
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={store.savingEdit}
                                    disabled={store.editingText.trim().length === 0}
                                    onClick={handleSaveEdit}
                                >
                                    Зберегти
                                </Button>
                                <Button size="small" onClick={() => store.cancelEdit()}>
                                    Скасувати
                                </Button>
                            </Space>
                        </Space>
                    ) : (
                        <div>
                            <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{comment.text}</Paragraph>
                            {renderFiles(comment)}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div>
            {canComment && (
                <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                    <TextArea
                        value={store.draftText}
                        onChange={(e) => store.setDraftText(e.target.value)}
                        rows={3}
                        maxLength={4096}
                        placeholder="Напишіть коментар…"
                    />
                    {store.draftFiles.length > 0 && (
                        <Space wrap>
                            {store.draftFiles.map((file) => (
                                <Tag key={file.uuid} closable onClose={() => void store.removeDraftFile(file.uuid)}>
                                    {file.originalName}
                                </Tag>
                            ))}
                        </Space>
                    )}
                    <Space>
                        <Upload customRequest={handleUpload} showUploadList={false} multiple>
                            <Button icon={<MdAttachFile />} loading={store.uploading}>
                                Файл
                            </Button>
                        </Upload>
                        <Button
                            type="primary"
                            loading={store.submitting}
                            disabled={store.draftText.trim().length === 0}
                            onClick={handleSubmit}
                        >
                            Додати коментар
                        </Button>
                    </Space>
                </Space>
            )}

            {store.loading ? (
                <Spin style={{ display: 'block', margin: '40px auto' }} />
            ) : store.comments.length === 0 ? (
                <SectionEmpty>Коментарів ще немає</SectionEmpty>
            ) : (
                <div>{store.comments.map(renderComment)}</div>
            )}
        </div>
    )
})
